import { useState, useEffect, useRef } from 'react';
import PackAnalysisSidebar from './PackAnalysisSidebar';
import Performance from './performance/Performance';
import Timeline from './timeline/Timeline';
import QsTypeBreakup from './qs-type-breakup/QsTypeBreakup';
import QualityOfAttempt from './quality-of-attempt/QualityOfAttempt';
import TimeAnalysis from './time-analysis/TimeAnalysis';
import DifficultyAnalysis from './difficulty-analysis/DifficultyAnalysis';
import ChapterAnalysis from './chapter-analysis/ChapterAnalysis';
import NoDataModal from '../components/modals/NoDataModal';
import { useDispatch, useSelector } from 'react-redux';
import { setRPack, fetchPack, setGroupId } from '../../../../apps/frontend/src/redux/slices/packSlice';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PhonePackAnalysisSidebar from './PhonePackAnalysisSidebar';
import { BottomNavigation } from './BottomNavigation';
import { AppLayout } from '../Layout/AppLayout';
import { resetFilters } from '../../../../apps/frontend/src/redux/slices/packSlice';
import { setTestFilter } from '../../../../apps/frontend/src/redux/slices/packSlice';
import { usePackStorage } from '../Layout/usePackStorage';

export default function PackAnalysisFinal() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialTestFilter = searchParams.get('testFilter');
  const navigate = useNavigate();
  const pathSegments = location.pathname.split('/');
  const lastSegment = pathSegments[pathSegments.length - 1];
  const dispatch = useDispatch();

  // Set currentScreen based on URL
  const validScreens = [
    'performance',
    'timeline',
    'qs-type-breakup',
    'quality-of-attempt',
    'time-analysis',
    'difficulty-analysis',
    'chapter-analysis',
  ];

  const [currentScreen, setCurrentScreen] = useState(validScreens.includes(lastSegment) ? lastSegment : 'performance');
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const [initialFilterSet, setInitialFilterSet] = useState(false);
  const isInitialMount = useRef(true);

  // Reset filters when component mounts
  useEffect(() => {
    // Reset filters to default when entering the pack analysis page
    dispatch(resetFilters());
    setSelectedTestTypes([]);
    setShowNoDataModal(false); // Reset modal state on mount
  }, []); // Empty dependency array - runs only on mount

  useEffect(() => {
    // Only set filter if it's last3 or last5
    if (initialTestFilter === 'last3' || initialTestFilter === 'last5') {
      dispatch(setTestFilter(initialTestFilter));
    }
  }, [initialTestFilter, dispatch]);

  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [isTabletHorizontal, setIsTabletHorizontal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(true);
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const fallbackPackId = typeof window !== 'undefined' ? localStorage.getItem('selectedPackId') : null;
  const params = useParams();
  const testId = params.testId;

  const {
    data: packAnalysisData,
    loading: packAnalysisLoading,
    error: packAnalysisError,
    filters: packFilters,
  } = useSelector((state) => state.pack.pack);

  const selectedGroupId = packFilters?.groupId;

  const [selectedTestTypes, setSelectedTestTypes] = useState([]);

  // Check if user has attempted any tests - WITH PROPER LOADING CHECK
  useEffect(() => {
    // Skip the initial mount to avoid showing modal with stale data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only check after loading is complete and we have fresh data
    if (!packAnalysisLoading && packAnalysisData) {
      // Check if there's any test attempted in the pack
      const hasAttemptedTests = packAnalysisData?.testAttemptedInPack > 0;

      // Show modal only if no tests attempted
      if (!hasAttemptedTests) {
        setShowNoDataModal(true);
      } else {
        setShowNoDataModal(false);
      }
    }
  }, [packAnalysisData, packAnalysisLoading]);

  useEffect(() => {
    // Update currentScreen when URL changes
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (validScreens.includes(lastSegment)) {
      setCurrentScreen(lastSegment);
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const isMobile = width <= 768;
      const isTabletPortrait = width > 768 && width <= 1366 && height > width;
      const isTabletHoriz = width > 768 && width <= 1366 && width > height;

      setIsMobileOrTablet(isMobile || isTabletPortrait);
      setIsTabletHorizontal(isTabletHoriz);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    if (selectedPack?._id) {
      dispatch(setRPack(selectedPack));
    }
  }, [selectedPack, dispatch]);

  const packIdToFetch = selectedPack?._id || fallbackPackId;

  useEffect(() => {
    if (packIdToFetch) {
      dispatch(fetchPack(packIdToFetch));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packIdToFetch, dispatch]);

  const handleScreenChange = (screen) => {
    if (searchParams.has('test-count')) {
      const testCountValue = searchParams.get('test-count');
      navigate(`/v3/pack-analysis/${screen}?test-count=${testCountValue}`);
    } else {
      navigate(`/v3/pack-analysis/${screen}`);
    }
    setCurrentScreen(screen);
    if (isMobileOrTablet) {
      setShowMobileMenu(false);
    }
  };

  const handleTestTypeChange = (newSelectedTypes) => {
    setSelectedTestTypes(newSelectedTypes);
    // Pass all selected group IDs, not just the first one
    dispatch(setGroupId(newSelectedTypes));
    const packId = selectedPack?._id || fallbackPackId;
    if (packId) {
      dispatch(fetchPack(packId));
    }
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
    setSelectedTestTypes([]);
  };

  const handleDrawerStateChange = (isOpen) => {
    setIsDrawerOpen(isOpen);
  };

  const handleBack = () => {
    if (isMobileOrTablet) {
      setShowMobileMenu(true);
    }
  };

  const handleCloseNoDataModal = () => {
    setShowNoDataModal(false);
  };

  const renderCurrentScreen = () => {
    const packId = selectedPack?._id || fallbackPackId;

    if (!packId) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <h2 className="text-xl font-bold mb-2">No Pack Selected</h2>
            <p className="text-gray-600">Please select a pack.</p>
          </div>
        </div>
      );
    }

    const commonProps = {
      packId,
      testId,
      onDrawerStateChange: handleDrawerStateChange,
      onBack: isMobileOrTablet ? handleBack : null,
    };

    switch (currentScreen) {
      case 'performance':
        return (
          <Performance
            {...commonProps}
            pack={selectedPack}
            isChapterWiseTestSeries={selectedPack?.isChapterWiseTestSeries || false}
            onResetFilters={handleResetFilters}
          />
        );
      case 'timeline':
        return <Timeline {...commonProps} onResetFilters={handleResetFilters} />;
      case 'qs-type-breakup':
        return <QsTypeBreakup {...commonProps} onResetFilters={handleResetFilters} />;
      case 'quality-of-attempt':
        return <QualityOfAttempt {...commonProps} onResetFilters={handleResetFilters} />;
      case 'time-analysis':
        return <TimeAnalysis {...commonProps} onResetFilters={handleResetFilters} />;
      case 'difficulty-analysis':
        return <DifficultyAnalysis {...commonProps} pack={selectedPack} onResetFilters={handleResetFilters} />;
      case 'chapter-analysis':
        return <ChapterAnalysis {...commonProps} onResetFilters={handleResetFilters} />;
      default:
        return (
          <Performance
            {...commonProps}
            pack={selectedPack}
            isChapterWiseTestSeries={selectedPack?.isChapterWiseTestSeries || false}
            onResetFilters={handleResetFilters}
          />
        );
    }
  };

  // Mobile and Tablet Portrait view
  if (isMobileOrTablet) {
    return (
      <div className="flex flex-col min-h-screen bg-[var(--q3-surface-default)] relative">
        {/* NoDataModal */}
        {showNoDataModal && (
          <div className=" z-[9999]">
            <NoDataModal onClose={handleCloseNoDataModal} />
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 pb-[80px] relative z-[1]">
          {showMobileMenu ? renderCurrentScreen() : renderCurrentScreen()}
        </div>

        {/* Bottom Navigation - always visible on mobile */}
        <BottomNavigation
          currentScreen={currentScreen}
          onScreenChange={handleScreenChange}
          selectedTestTypes={selectedTestTypes}
          onTestTypeChange={handleTestTypeChange}
          loading={packAnalysisLoading}
          categories={packAnalysisData?.categories || []}
          subjects={packAnalysisData?.subjects || []}
          apiResponse={packAnalysisData ? { data: packAnalysisData } : null}
          onResetFilters={handleResetFilters}
        />
      </div>
    );
  }

  // Tablet Horizontal view
  if (isTabletHorizontal) {
    return (
      <div className="flex flex-col min-h-screen bg-[var(--q3-surface-default)] relative">
        {/* NoDataModal */}
        {showNoDataModal && <NoDataModal onClose={handleCloseNoDataModal} />}

        {/* Main Content */}
        <div className="flex-1 min-w-0 pb-[80px] relative z-[1]">{renderCurrentScreen()}</div>

        {/* Bottom Navigation */}
        <BottomNavigation
          currentScreen={currentScreen}
          onScreenChange={handleScreenChange}
          selectedTestTypes={selectedTestTypes}
          onTestTypeChange={handleTestTypeChange}
          loading={packAnalysisLoading}
          categories={packAnalysisData?.categories || []}
          subjects={packAnalysisData?.subjects || []}
          apiResponse={packAnalysisData ? { data: packAnalysisData } : null}
          onResetFilters={handleResetFilters}
        />
      </div>
    );
  }

  // Desktop view
  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      {/* NoDataModal */}
      {showNoDataModal && <NoDataModal onClose={handleCloseNoDataModal} />}

      <div className="flex-1 h-screen relative z-[1]">
        <div className="w-full flex flex-col items-center">
          <div className="w-7xl flex flex-col md:flex-row gap-6 justify-center">
            {/* Pack Analysis Sidebar (desktop version) */}
            <div
              className={`hidden md:block w-80 pl-0 md:ml-3 shrink-0 relative transition-all duration-300 ${isDrawerOpen ? 'z-[40]' : 'z-[40]'}`}
            >
              <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
                <PackAnalysisSidebar
                  currentScreen={currentScreen}
                  onScreenChange={handleScreenChange}
                  selectedTestTypes={selectedTestTypes}
                  onTestTypeChange={handleTestTypeChange}
                  groups={packAnalysisData?.groups || []}
                  subjects={packAnalysisData?.subjects || []}
                  categories={packAnalysisData?.categories || []}
                  packTitle={packAnalysisData?.title || selectedPack?.name || 'Quizrr Mock Tests Pack Analysis'}
                  loading={packAnalysisLoading}
                  packId={selectedPack?._id || fallbackPackId}
                  testId={testId}
                  apiResponse={packAnalysisData ? { data: packAnalysisData } : null}
                  onResetFilters={handleResetFilters}
                />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 px-4 lg:px-0 md:pb-10 pb-20 w-full relative z-[1]">{renderCurrentScreen()}</div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
